import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import { addToWatchlist, ApiError, clearWatchlist, removeFromWatchlist, type WatchlistEntry, watchlistQueries } from '@/api';
import { toastError } from '@/components/ui';
import { useSession } from '@/hooks/use-session';

const KEY = 'sirius.watchlist.v1';
const EMPTY: string[] = [];

/*
  Гостьовий список — у браузері. Зберігаємо ЛИШЕ ідентифікатори: це
  вподобання, а не серверні дані (§26 гайду). Після входу список переїжджає
  в акаунт (`WatchlistSync`), і далі джерело правди — база.

  Кожен доступ у try/catch: у приватному режимі та за заблокованих site data
  навіть читання кидає виняток.
*/
function read(): string[] {
	try {
		const raw = localStorage.getItem(KEY);
		if (!raw) return EMPTY;
		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : EMPTY;
	} catch {
		return EMPTY;
	}
}

let cache: string[] = read();
const listeners = new Set<() => void>();

function emit(): void {
	cache = read();
	for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	// Синхронізація між вкладками: у сусідній вкладці міг змінитись той самий список.
	const onStorage = (e: StorageEvent): void => {
		if (e.key === KEY) emit();
	};
	window.addEventListener('storage', onStorage);
	return (): void => {
		listeners.delete(listener);
		window.removeEventListener('storage', onStorage);
	};
}

/** Гостьовий список — для перенесення в акаунт після входу. */
export function readGuestWatchlist(): string[] {
	return cache;
}

export function writeGuestWatchlist(ids: string[]): void {
	try {
		if (ids.length === 0) localStorage.removeItem(KEY);
		else localStorage.setItem(KEY, JSON.stringify(ids));
	} catch {
		// Немає сховища — список просто не переживе перезавантаження. Не привід ламати сторінку.
	}
	emit();
}

export function useGuestWatchlistIds(): string[] {
	return useSyncExternalStore(subscribe, () => cache, () => EMPTY);
}

const ADD_KEY = ['watchlist', 'add'] as const;
const REMOVE_KEY = ['watchlist', 'remove'] as const;

function failureText(error: unknown): string {
	if (error instanceof ApiError) {
		if (error.code === 'LOT_NOT_FOUND') return 'This lot has left the auction, so it can’t be saved.';
		if (error.code === 'WATCHLIST_FULL') return error.message;
	}
	return 'Please try again in a moment.';
}

export interface WatchlistApi {
	ids: string[];
	count: number;
	/**
	 * Записи акаунта з копіями лотів. null — гість: у нього лише ідентифікатори
	 * в браузері, і копій немає.
	 */
	entries: WatchlistEntry[] | null;
	/** Список акаунта ще не прийшов. Для гостя завжди false. */
	isPending: boolean;
	has: (id: string) => boolean;
	toggle: (id: string) => void;
	remove: (id: string) => void;
	clear: () => void;
}

/**
 * Watchlist: для дилера — у базі з копією лота (інваріант «копія знімається
 * в момент наміру»), для гостя — у браузері.
 *
 * Зірка реагує миттєво: до відповіді сервера лот уже рахується доданим або
 * прибраним. Це свідомий виняток із правила «без оптимістичних оновлень»
 * (§7.3): перемикач тривіально передбачуваний, а додавання питає провайдера
 * і на тарифі Basic займає до кількох секунд. Кеш при цьому не підробляється —
 * очікувані зміни читаються зі стану мутацій і зникають разом із ними.
 */
export function useWatchlist(): WatchlistApi {
	const { isAuthenticated } = useSession();
	const guestIds = useGuestWatchlistIds();
	const queryClient = useQueryClient();
	const { data: entries } = useQuery({ ...watchlistQueries.list(), enabled: isAuthenticated });

	const settle = (): Promise<void> => queryClient.invalidateQueries({ queryKey: watchlistQueries.all() });
	const add = useMutation({
		mutationKey: ADD_KEY,
		mutationFn: addToWatchlist,
		onError: (error) => toastError('Couldn’t save this lot', failureText(error)),
		onSettled: settle,
	});
	const removeOne = useMutation({
		mutationKey: REMOVE_KEY,
		mutationFn: removeFromWatchlist,
		onError: () => toastError('Couldn’t remove this lot', 'Please try again in a moment.'),
		onSettled: settle,
	});
	const clearAll = useMutation({
		mutationFn: clearWatchlist,
		onError: () => toastError('Couldn’t clear your watchlist', 'Please try again in a moment.'),
		onSettled: settle,
	});

	const adding = useMutationState({ filters: { mutationKey: ADD_KEY, status: 'pending' }, select: (m) => m.state.variables as string });
	const removing = useMutationState({ filters: { mutationKey: REMOVE_KEY, status: 'pending' }, select: (m) => m.state.variables as string });

	const serverIds = (entries ?? []).map((entry) => entry.vehicleId);
	const ids = isAuthenticated
		? [...serverIds.filter((id) => !removing.includes(id)), ...adding.filter((id) => !serverIds.includes(id))]
		: guestIds;

	const drop = (id: string): void => {
		if (isAuthenticated) removeOne.mutate(id);
		else writeGuestWatchlist(guestIds.filter((x) => x !== id));
	};

	return {
		ids,
		count: ids.length,
		entries: isAuthenticated ? (entries ?? []) : null,
		isPending: isAuthenticated && entries === undefined,
		has: (id): boolean => ids.includes(id),
		toggle: (id): void => {
			if (ids.includes(id)) drop(id);
			else if (isAuthenticated) add.mutate(id);
			else writeGuestWatchlist([...guestIds, id]);
		},
		remove: drop,
		clear: (): void => {
			if (isAuthenticated) clearAll.mutate();
			else writeGuestWatchlist([]);
		},
	};
}
